import { useState, useEffect, useRef } from "react";
import { Navbar, Nav, Container, Button } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./NavBar.css";
import { useAuth } from "../../context/AuthContext";

const NavBar = () => {
  const { isLoggedIn, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) {
        setExpanded(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Close navbar when navigating on mobile
    if (isMobile) {
    setExpanded(false);
      setShowServicesDropdown(false);
    }
  }, [location, isMobile]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowServicesDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMouseEnter = () => {
    if (!isMobile && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowServicesDropdown(true);
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
    timeoutRef.current = setTimeout(() => {
      setShowServicesDropdown(false);
    }, 300);
    }
  };

  const toggleServicesDropdown = (e: React.MouseEvent) => {
    if (isMobile) {
      e.preventDefault();
      setShowServicesDropdown(!showServicesDropdown);
    }
  };

  const serviceItems = [
    {
      title: "FTO Report",
      description:
        "Comprehensive freedom-to-operate analysis for your innovations",
      path: "/fto-report",
      icon: "bi-shield-check",
    },
    {
      title: "Prior Art Report",
      description: "Discover existing technologies related to your invention",
      path: "/prior-art-report",
      icon: "bi-search",
    },
    {
      title: "Validity Search",
      description: "Analyze patents to determine enforceability and strength",
      path: "/validity-search",
      icon: "bi-check-circle",
    },
    {
      title: "Compound Intelligence",
      description: "Discover new drug candidates and optimize existing ones",
      path: "/compound-intelligence",
      icon: "bi-capsule",
    },
  ];

  return (
    <Navbar
      bg="light"
      expand="lg"
      className={`py-3 shadow-sm sticky-top ${scrolled ? "scrolled" : ""}`}
      expanded={expanded}
      onToggle={(isExpanded) => setExpanded(isExpanded)}
    >
      <Container className="d-flex justify-content-between align-items-center">
        {/* Always show logo */}
        <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
          <img
            src={`/fyled.png`}
            alt="Fyled AI Logo"
            width="40"
            height="40"
            className="d-inline-block align-top me-2"
          />
          <span>Fyled AI</span>
        </Navbar.Brand>

        {/* Responsive navbar toggler */}
        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Navbar.Collapse id="basic-navbar-nav">
          {!isLoggedIn ? (
            <>
              <Nav className="me-auto">
                <Nav.Link
                  as={Link}
                  to="/about"
                  className={location.pathname === "/about" ? "active" : ""}
                >
                  About
                </Nav.Link>
                {/* Custom Dropdown */}
                <div
                  ref={dropdownRef}
                  className="nav-item dropdown position-static"
                  onMouseEnter={!isMobile ? handleMouseEnter : undefined}
                  onMouseLeave={!isMobile ? handleMouseLeave : undefined}
                >
                  <Nav.Link
                    className={`dropdown-toggle ${
                      location.pathname.includes("/services") ? "active" : ""
                    }`}
                    onClick={toggleServicesDropdown}
                  >
                    Services
                  </Nav.Link>

                  <div
                    className={`services-dropdown-menu dropdown-menu p-0 shadow ${
                      showServicesDropdown ? "show" : ""
                    }`}
                  >
                    <div className="dropdown-inner">
                      {serviceItems.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="service-item text-decoration-none"
                          onClick={() => {
                            setShowServicesDropdown(false);
                            setExpanded(false);
                          }}
                        >
                          <div className="service-icon">
                            <i className={`bi ${service.icon}`}></i>
                          </div>
                          <div className="service-content">
                            <h5 className="service-title">{service.title}</h5>
                            <p className="service-description">
                              {service.description}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                <Nav.Link
                  as={Link}
                  to="/contact"
                  className={location.pathname === "/contact" ? "active" : ""}
                >
                  Contact Us
                </Nav.Link>

                <Nav.Link
                  as={Link}
                  to="/demo"
                  className={location.pathname === "/demo" ? "active" : ""}
                >
                  Demo
                </Nav.Link>
              </Nav>

              <Nav className="ms-auto">
              <Link to="/sign-up">
                  <Button variant="outline-primary" className="me-2">
                    Sign up
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="primary" className="me-2">
                    Login
                  </Button>
                </Link>
              </Nav>
            </>
          ) : (
            <Nav className="ms-auto">
              <Button variant="outline-danger" onClick={logout}>
                Logout
              </Button>
            </Nav>
          )}
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavBar;
